import venueApiService from '../services/venueApi';
import venueDashboardApiService from '../services/venueDashboardApi';

const VENUE_DISCOVERY_CACHE_TTL_MS = 10 * 60 * 1000;
/** Bump when discovery bundle shape or booking-rules semantics change. */
export const VENUE_DISCOVERY_CACHE_SCHEMA_VERSION = 3;
const SESSION_STORAGE_PREFIX = 'occasia:venue-discovery-bundle:';
const venueDiscoveryCache = new Map();
const warmInFlight = new Map();

const readSessionBundle = (venueId) => {
  const key = venueId != null ? String(venueId) : '';
  if (!key) return null;
  try {
    const raw = sessionStorage.getItem(`${SESSION_STORAGE_PREFIX}${key}`);
    if (!raw) return null;
    const entry = JSON.parse(raw);
    if (!entry?.bundle || !entry?.fetchedAt) return null;
    if (Date.now() - entry.fetchedAt > VENUE_DISCOVERY_CACHE_TTL_MS) {
      sessionStorage.removeItem(`${SESSION_STORAGE_PREFIX}${key}`);
      return null;
    }
    return entry;
  } catch {
    return null;
  }
};

const writeSessionBundle = (venueId, bundle, fetchedAt = Date.now()) => {
  const key = venueId != null ? String(venueId) : '';
  if (!key || !bundle) return;
  try {
    sessionStorage.setItem(
      `${SESSION_STORAGE_PREFIX}${key}`,
      JSON.stringify({ bundle, fetchedAt })
    );
  } catch {
    // Quota exceeded or private mode — in-memory cache still works.
  }
};

const removeSessionBundle = (venueId) => {
  const key = venueId != null ? String(venueId) : '';
  if (!key) return;
  try {
    sessionStorage.removeItem(`${SESSION_STORAGE_PREFIX}${key}`);
  } catch {
    // ignore
  }
};

export const getCachedVenueDiscoveryBundle = (venueId) => {
  const key = venueId != null ? String(venueId) : '';
  if (!key) return null;

  const isValidBundle = (bundle) =>
    bundle?.schemaVersion === VENUE_DISCOVERY_CACHE_SCHEMA_VERSION;

  const memoryEntry = venueDiscoveryCache.get(key);
  if (memoryEntry) {
    if (Date.now() - memoryEntry.fetchedAt > VENUE_DISCOVERY_CACHE_TTL_MS) {
      venueDiscoveryCache.delete(key);
    } else if (isValidBundle(memoryEntry.bundle)) {
      return memoryEntry.bundle;
    } else {
      venueDiscoveryCache.delete(key);
    }
  }

  const sessionEntry = readSessionBundle(key);
  if (sessionEntry?.bundle) {
    if (isValidBundle(sessionEntry.bundle)) {
      venueDiscoveryCache.set(key, sessionEntry);
      return sessionEntry.bundle;
    }
    removeSessionBundle(key);
  }

  return null;
};

export const setCachedVenueDiscoveryBundle = (venueId, bundle) => {
  const key = venueId != null ? String(venueId) : '';
  if (!key || !bundle) return;
  const entry = { bundle, fetchedAt: Date.now() };
  venueDiscoveryCache.set(key, entry);
  writeSessionBundle(key, bundle, entry.fetchedAt);
};

export const clearVenueDiscoveryCache = (venueId) => {
  if (venueId != null) {
    const key = String(venueId);
    venueDiscoveryCache.delete(key);
    removeSessionBundle(key);
    return;
  }
  venueDiscoveryCache.clear();
  try {
    Object.keys(sessionStorage).forEach((storageKey) => {
      if (storageKey.startsWith(SESSION_STORAGE_PREFIX)) {
        sessionStorage.removeItem(storageKey);
      }
    });
  } catch {
    // ignore
  }
};

export const buildVenueDiscoveryBundle = ({
  venueData,
  pricing = [],
  spacesData = [],
  operatingHoursData = {},
  operatingHoursPerPersonData = {},
  holidaysData = [],
  flatPricingData = [],
  defaultPerPersonData = null,
  dynamicPerPersonData = [],
  bookingRulesData = null
}) => ({
  schemaVersion: VENUE_DISCOVERY_CACHE_SCHEMA_VERSION,
  venueData,
  pricing: Array.isArray(pricing) ? pricing : [],
  spacesData: Array.isArray(spacesData) ? spacesData : [],
  operatingHoursData:
    typeof operatingHoursData === 'object' && operatingHoursData !== null ? operatingHoursData : {},
  operatingHoursPerPersonData:
    typeof operatingHoursPerPersonData === 'object' && operatingHoursPerPersonData !== null
      ? operatingHoursPerPersonData
      : {},
  holidaysData: Array.isArray(holidaysData) ? holidaysData : [],
  flatPricingData: Array.isArray(flatPricingData) ? flatPricingData : [],
  defaultPerPersonData,
  dynamicPerPersonData: Array.isArray(dynamicPerPersonData) ? dynamicPerPersonData : [],
  bookingRulesData: bookingRulesData ?? null
});

export const fetchVenueDiscoveryBundle = async (venueId, { venueData = null, forceRefresh = false } = {}) => {
  const id = venueId != null ? String(venueId) : '';
  if (!id) throw new Error('Venue ID is required');

  const results = await Promise.allSettled([
    venueData ? Promise.resolve(venueData) : venueApiService.getVenueById(id, { forceRefresh }),
    venueApiService.getDynamicPricing(id),
    venueDashboardApiService.getVenueSpaces(id),
    venueDashboardApiService.getOperatingHours(id),
    venueDashboardApiService.getOperatingHoursPerPerson(id),
    venueDashboardApiService.getHolidays(id),
    venueDashboardApiService.getFlatPricing(id),
    venueDashboardApiService.getDefaultPerPersonPrice(id),
    venueDashboardApiService.getDynamicPricingPerPerson(id),
    venueApiService.getBookingRules(id)
  ]);

  return buildVenueDiscoveryBundle({
    venueData: results[0].status === 'fulfilled' ? results[0].value : venueData,
    pricing: results[1].status === 'fulfilled' ? results[1].value : [],
    spacesData: results[2].status === 'fulfilled' ? results[2].value : [],
    operatingHoursData: results[3].status === 'fulfilled' ? results[3].value : {},
    operatingHoursPerPersonData: results[4].status === 'fulfilled' ? results[4].value : {},
    holidaysData: results[5].status === 'fulfilled' ? results[5].value : [],
    flatPricingData: results[6].status === 'fulfilled' ? results[6].value : [],
    defaultPerPersonData: results[7].status === 'fulfilled' ? results[7].value : null,
    dynamicPerPersonData: results[8].status === 'fulfilled' ? results[8].value : [],
    bookingRulesData: results[9].status === 'fulfilled' ? results[9].value : null
  });
};

export const warmVenueDiscoveryCache = (venueId) => {
  const id = venueId != null ? String(venueId) : '';
  if (!id || getCachedVenueDiscoveryBundle(id) || warmInFlight.has(id)) return;

  const promise = fetchVenueDiscoveryBundle(id)
    .then((bundle) => {
      setCachedVenueDiscoveryBundle(id, bundle);
      return bundle;
    })
    .finally(() => {
      warmInFlight.delete(id);
    });

  warmInFlight.set(id, promise);
};

export const venueIdsMatch = (left, right) =>
  left != null && right != null && String(left) === String(right);
